import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Shield, 
  Activity, Target, Clock, ChevronRight, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { getRiskDashboard, getRiskHeatmap, RISK_CATEGORIES } from '@/services/risk';
import { toast } from 'sonner';

const RiskDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMatrix, setActiveMatrix] = useState('inherent');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashData, heatData] = await Promise.all([
        getRiskDashboard(),
        getRiskHeatmap()
      ]);
      setDashboard(dashData);
      setHeatmap(heatData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getCellColor = (row, col) => {
    const score = (row + 1) * (col + 1);
    if (score >= 20) return 'bg-red-500';
    if (score >= 12) return 'bg-orange-500';
    if (score >= 6) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getRiskCount = (row, col) => {
    if (!heatmap) return 0;
    const matrix = activeMatrix === 'inherent' ? heatmap.inherent_matrix : heatmap.residual_matrix;
    return matrix?.[row]?.[col]?.length || 0;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Risk Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of organizational risk posture</p>
        </div>
        <Link to="/grc/risk/register">
          <Button className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            View Risk Register
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Risks</p>
                <p className="text-3xl font-bold">{dashboard?.total_risks || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Critical/High</p>
                <p className="text-3xl font-bold text-red-600">
                  {(dashboard?.risks_by_level?.critical || 0) + (dashboard?.risks_by_level?.high || 0)}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Target className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open Treatments</p>
                <p className="text-3xl font-bold text-yellow-600">{dashboard?.open_treatments || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue Treatments</p>
                <p className="text-3xl font-bold text-orange-600">{dashboard?.overdue_treatments || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Heatmap */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Risk Heatmap
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant={activeMatrix === 'inherent' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setActiveMatrix('inherent')}
                >
                  Inherent
                </Button>
                <Button 
                  variant={activeMatrix === 'residual' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setActiveMatrix('residual')}
                >
                  Residual
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex">
              {/* Y-axis label */}
              <div className="flex flex-col justify-center mr-2">
                <span className="text-xs text-gray-500 transform -rotate-90 whitespace-nowrap">
                  Likelihood →
                </span>
              </div>
              <div className="flex-1">
                {/* Matrix */}
                <div className="grid grid-cols-5 gap-1">
                  {[4, 3, 2, 1, 0].map(row => (
                    [0, 1, 2, 3, 4].map(col => {
                      const count = getRiskCount(row, col);
                      return (
                        <div
                          key={`${row}-${col}`}
                          className={`aspect-square ${getCellColor(row, col)} rounded flex items-center justify-center text-white font-medium text-sm cursor-pointer hover:opacity-80 transition-opacity`}
                          title={`L${row + 1} x I${col + 1} = ${(row + 1) * (col + 1)}`}
                        >
                          {count > 0 ? count : ''}
                        </div>
                      );
                    })
                  ))}
                </div>
                {/* X-axis label */}
                <div className="text-center mt-2">
                  <span className="text-xs text-gray-500">Impact →</span>
                </div>
              </div>
            </div>
            {/* Legend */}
            <div className="flex justify-center gap-4 mt-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-xs text-gray-600">Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span className="text-xs text-gray-600">Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded"></div>
                <span className="text-xs text-gray-600">High</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span className="text-xs text-gray-600">Critical</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KRI Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Key Risk Indicators
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="font-medium">Within Tolerance</span>
                </div>
                <span className="text-2xl font-bold text-green-600">{dashboard?.kri_status?.green || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="font-medium">Warning</span>
                </div>
                <span className="text-2xl font-bold text-yellow-600">{dashboard?.kri_status?.amber || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="font-medium">Breached</span>
                </div>
                <span className="text-2xl font-bold text-red-600">{dashboard?.kri_status?.red || 0}</span>
              </div>
            </div>
            <Link to="/grc/risk/kris" className="block mt-4">
              <Button variant="outline" className="w-full">
                View All KRIs <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Risks by Category */}
        <Card>
          <CardHeader>
            <CardTitle>Risks by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {RISK_CATEGORIES.slice(0, 6).map(cat => {
                const count = dashboard?.risks_by_category?.[cat.value] || 0;
                const percentage = dashboard?.total_risks ? (count / dashboard.total_risks * 100) : 0;
                return (
                  <div key={cat.value} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-28 truncate">{cat.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Risks */}
        <Card>
          <CardHeader>
            <CardTitle>Top Risks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboard?.top_risks || []).slice(0, 5).map((risk, idx) => (
                <Link 
                  key={risk.id} 
                  to={`/grc/risk/register/${risk.id}`}
                  className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{risk.title}</p>
                    <p className="text-xs text-gray-500">{risk.risk_id}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    risk.inherent_risk_score >= 20 ? 'bg-red-100 text-red-700' :
                    risk.inherent_risk_score >= 12 ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {risk.inherent_risk_score}
                  </span>
                </Link>
              ))}
              {(!dashboard?.top_risks || dashboard.top_risks.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">No risks found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Link to="/grc/risk/register">
              <Button variant="outline" className="gap-2">
                <AlertTriangle className="w-4 h-4" />
                Risk Register
              </Button>
            </Link>
            <Link to="/grc/risk/assessments">
              <Button variant="outline" className="gap-2">
                <Activity className="w-4 h-4" />
                Assessments
              </Button>
            </Link>
            <Link to="/grc/risk/treatments">
              <Button variant="outline" className="gap-2">
                <Shield className="w-4 h-4" />
                Treatments
              </Button>
            </Link>
            <Link to="/grc/risk/appetite">
              <Button variant="outline" className="gap-2">
                <Target className="w-4 h-4" />
                Risk Appetite
              </Button>
            </Link>
            <Link to="/grc/risk/kris">
              <Button variant="outline" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                KRIs
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RiskDashboard;
